import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CONTRACT_CATEGORY_OPTIONS, LIGHTSPARK_ENTITY_FILTER_OPTIONS as LIGHTSPARK_ENTITIES } from '../../constants/contracts';
import { CONTRACT_STATUSES } from '../../constants/statuses';
import { apiDownload, apiMaybe, apiRequest } from '../../services/apiClient';
import { normalizeSearchValue, readOptionParam, readSearchParam } from '../../utils/urlFilters';
import useUrlSyncedFilters, { useReseedPageGuard } from '../../hooks/useUrlSyncedFilters';
import {
  contractTypesToOptions,
  counterpartyGroupsToOptions,
  getContractFacets,
  getContractTypes,
  tagsToOptions,
} from './contractLookupApi';

export const PAGE_SIZE_OPTIONS = [25, 50, 100];

export const DEADLINE_WINDOW_OPTIONS = [30, 60, 90];
export const DEFAULT_DEADLINE_WINDOW = 60;

function parsePage(value) {
  const page = parseInt(value, 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
}

// The deadline window lives in the URL (?deadline=30|60|90) instead of being
// hardcoded, so a link can open this list on the SAME window as the number
// that sent the user here. It used to be a fixed 60: the dashboard's
// "Upcoming deadlines" card is a 90-day set, so its own "View all" opened a
// 60-day list that was missing rows the card had just shown (doc-140).
// Anything else — absent, junk, an unlisted 45 — falls back to 60 so existing
// ?filter=deadline bookmarks keep behaving exactly as before.
export function parseDeadlineWindow(value) {
  // Number(), not parseInt() — parseInt('90abc') is 90, which would let junk
  // through the whitelist on its numeric prefix.
  const days = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  return DEADLINE_WINDOW_OPTIONS.includes(days) ? days : DEFAULT_DEADLINE_WINDOW;
}

// The backend query params for a given filter/sort state. Pure so the window
// plumbing is unit-testable without standing up the hook.
export function buildContractQueryParams({
  category,
  counterparty,
  deadlineFilter,
  deadlineWindow,
  entity,
  expiringFilter,
  group,
  needsReview,
  noticeFilter,
  renewalReviewNeeded,
  search,
  sortField,
  sortOrder,
  source,
  status,
  tag,
  type,
}) {
  const params = new URLSearchParams();
  // Trimmed, so the term in the URL, the term in the box and the term on the
  // wire are the same one — a whitespace-only search is no search (doc-194).
  const trimmedSearch = normalizeSearchValue(search);
  if (trimmedSearch) params.set('search', trimmedSearch);
  if (status) params.set('status', status);
  if (type) params.set('type', type);
  if (category) params.set('category', category);
  if (entity) params.set('entity', entity);
  if (counterparty) params.set('counterparty', counterparty);
  if (group) params.set('counterpartyGroup', group);
  if (tag) params.set('tags', tag);
  // One window drives all three deadline kinds — they're mutually exclusive
  // in the UI, so there's never more than one of these on a request.
  const windowDays = String(parseDeadlineWindow(deadlineWindow));
  if (expiringFilter) params.set('expiring', windowDays);
  if (noticeFilter) params.set('notice', windowDays);
  if (deadlineFilter) params.set('deadline', windowDays);
  if (needsReview) params.set('needsReview', 'true');
  if (renewalReviewNeeded) params.set('renewalReviewNeeded', 'true');
  if (source) params.set('source', source);
  if (sortField) params.set('sort', sortField);
  if (sortOrder) params.set('order', sortOrder);
  return params;
}

function getStoredPageSize() {
  const stored = localStorage.getItem('contractsPageSize');
  const pageSize = stored ? parseInt(stored, 10) : 25;
  return PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : 25;
}

// The ONE definition of "what this list's filters are, given a querystring".
// Used by the useState initializers at mount AND by the re-seed when the URL
// changes from outside (doc-195). This list has 18 URL-backed values, which is
// exactly why a second hand-written copy of the parse rules is not an option —
// they would drift, and doc-194's option-list validation, doc-168's trimmed
// search and doc-140's deadline-window fallback would drift with them.
//
// The three-way filter is one URL key (?filter=expiring|notice|deadline) fanned
// out into three booleans, and pageSize is deliberately absent: it is a
// localStorage preference, never a URL param.
function seedFromParams(params) {
  return {
    search: readSearchParam(params, 'search'),
    // status/category/entity validate against their STATIC option lists
    // (doc-194): a value the control cannot display must not be applied
    // invisibly. type, counterparty, group and tag are deliberately NOT
    // validated — their options arrive from a later facet fetch, so checking
    // them here would scrub every legitimate deep-linked value.
    status: readOptionParam(params, 'status', CONTRACT_STATUSES),
    type: params.get('type') || '',
    category: readOptionParam(params, 'category', CONTRACT_CATEGORY_OPTIONS),
    entity: readOptionParam(params, 'entity', LIGHTSPARK_ENTITIES),
    counterparty: params.get('counterparty') || '',
    group: params.get('group') || '',
    tag: params.get('tag') || '',
    expiringFilter: params.get('filter') === 'expiring',
    noticeFilter: params.get('filter') === 'notice',
    deadlineFilter: params.get('filter') === 'deadline',
    deadlineWindow: parseDeadlineWindow(params.get('deadline')),
    needsReview: params.get('needsReview') === 'true',
    renewalReviewNeeded: params.get('renewalReviewNeeded') === 'true',
    source: params.get('source') || '',
    sortField: params.get('sort') || 'updatedAt',
    sortOrder: params.get('order') || 'desc',
    page: parsePage(params.get('page')),
  };
}

// Identity of "which filters are applied", for the page-reset guard. Field names
// match seedFromParams, so the live-state call and the re-seed call cannot
// disagree about what counts as a filter change. Sort is absent on purpose — it
// is not in the reset effect's deps either, since re-sorting keeps your page.
function filtersKeyOf({
  search, status, type, category, entity, counterparty, group, tag,
  expiringFilter, noticeFilter, deadlineFilter, deadlineWindow, needsReview,
  renewalReviewNeeded, source,
}) {
  return JSON.stringify([
    search, status, type, category, entity, counterparty, group, tag,
    expiringFilter, noticeFilter, deadlineFilter, deadlineWindow, needsReview,
    renewalReviewNeeded, source,
  ]);
}

export default function useContractsListData() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contractTypeOptions, setContractTypeOptions] = useState([{ value: '', label: 'All types' }]);
  const [seed] = useState(() => seedFromParams(searchParams));
  const [search, setSearch] = useState(seed.search);
  const [status, setStatus] = useState(seed.status);
  const [type, setType] = useState(seed.type);
  const [category, setCategory] = useState(seed.category);
  const [entity, setEntity] = useState(seed.entity);
  const [counterparty, setCounterparty] = useState(seed.counterparty);
  const [group, setGroup] = useState(seed.group);
  const [groupOptions, setGroupOptions] = useState([{ value: '', label: 'All groups' }]);
  const [tag, setTag] = useState(seed.tag);
  const [tagOptions, setTagOptions] = useState([{ value: '', label: 'All tags' }]);
  const [expiringFilter, setExpiringFilter] = useState(seed.expiringFilter);
  const [noticeFilter, setNoticeFilter] = useState(seed.noticeFilter);
  const [deadlineFilter, setDeadlineFilter] = useState(seed.deadlineFilter);
  const [deadlineWindow, setDeadlineWindow] = useState(seed.deadlineWindow);
  const [needsReview, setNeedsReview] = useState(seed.needsReview);
  const [renewalReviewNeeded, setRenewalReviewNeeded] = useState(seed.renewalReviewNeeded);
  const [source, setSource] = useState(seed.source);
  const [sortField, setSortField] = useState(seed.sortField);
  const [sortOrder, setSortOrder] = useState(seed.sortOrder);
  const [page, setPage] = useState(seed.page);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [statusCounts, setStatusCounts] = useState({});
  // Bumped by retry() so the fetch effect re-runs with identical filters
  // after a failed load — without this the user's only recourse is a hard refresh.
  const [retryToken, setRetryToken] = useState(0);
  const retry = useCallback(() => setRetryToken((token) => token + 1), []);
  // Lookup endpoints resolve null on failure (apiMaybe) — the list still works,
  // but type/group/tag dropdowns would silently render empty. Track it so the
  // page can tell the user instead of pretending there's nothing to filter by.
  const [lookupsDegraded, setLookupsDegraded] = useState(false);
  // Bulk-select over the current page. Cleared whenever the list refetches
  // (filter/sort/page change or an explicit refresh) — a selection made
  // against one result set must not silently carry over to another.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Bumped by refresh() to refetch the list and the count/tag lookups in
  // place after a bulk mutation, without touching filters or page.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    getContractTypes().then((data) => {
      if (!data) {
        setLookupsDegraded(true);
        return;
      }
      setContractTypeOptions(contractTypesToOptions(data.contractTypes, { value: '', label: 'All types' }));
    });
  }, []);

  useEffect(() => {
    getContractFacets().then((data) => {
      if (!data) {
        setLookupsDegraded(true);
        return;
      }
      if (Array.isArray(data.counterpartyGroups)) {
        setGroupOptions(counterpartyGroupsToOptions(data.counterpartyGroups, { value: '', label: 'All groups' }));
      }
      if (Array.isArray(data.tags)) {
        setTagOptions(tagsToOptions(data.tags, { value: '', label: 'All tags' }));
      }
    });
    // reloadKey: bulk tag edits change the available tag facets.
  }, [reloadKey]);

  useEffect(() => {
    // Counts label the status tabs — a failure is logged by apiMaybe but
    // doesn't warrant the degraded-filters notice.
    apiMaybe('/dashboard').then((data) => {
      if (!data) return;
      // byStatus is the live portfolio and never carries a terminated key, so
      // that count arrives separately. Last wins, which keeps this correct even
      // if byStatus ever starts emitting one.
      setStatusCounts({
        all: data.totalContracts || 0,
        ...(data.byStatus || {}),
        terminated: data.terminatedContracts || 0,
      });
    });
    // reloadKey: bulk status changes shift the per-status counts.
  }, [reloadKey]);

  // Reset to page 1 whenever a filter or the search term changes, so you can
  // never land on page 4 of a filter that only has one page — but NOT on mount,
  // or a deep-linked ?page=2 gets clobbered before the first fetch (doc-214);
  // and not when the change is a re-seed landing, which carries its own page
  // (doc-224). Both guards in this order are the idiom doc-224 shipped on
  // RequestsList / ReviewsList — mount guard first, so a mount never consumes a
  // pending re-seed key. The four sibling lists all carry the mount guard; this
  // hook was the outlier.
  const { markReseeded, isReseedArrival } = useReseedPageGuard();
  const filtersTouchedRef = useRef(false);
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    const key = filtersKeyOf({
      search, status, type, category, entity, counterparty, group, tag,
      expiringFilter, noticeFilter, deadlineFilter, deadlineWindow, needsReview,
      renewalReviewNeeded, source,
    });
    if (isReseedArrival(key)) return;
    setPage(1);
  }, [
    category,
    counterparty,
    deadlineFilter,
    deadlineWindow,
    entity,
    expiringFilter,
    group,
    // Stable (useCallback with no deps), so listing it does not re-run this.
    isReseedArrival,
    needsReview,
    noticeFilter,
    renewalReviewNeeded,
    search,
    source,
    status,
    tag,
    type,
  ]);

  // Re-seed when the querystring changes from OUTSIDE this hook — the sidebar
  // "Contracts" link while already on /contracts, a CommandK jump, a dashboard
  // card linking back to the list you are on. Without it the URL blanks and all
  // 18 controls keep their old values (doc-195).
  const markUrlWritten = useUrlSyncedFilters({
    searchParams,
    onExternalChange: useCallback((params) => {
      const next = seedFromParams(params);
      // Before the state updates, so the page-reset effect recognises the
      // filter change they cause and leaves next.page alone.
      markReseeded(filtersKeyOf(next));
      setSearch(next.search);
      setStatus(next.status);
      setType(next.type);
      setCategory(next.category);
      setEntity(next.entity);
      setCounterparty(next.counterparty);
      setGroup(next.group);
      setTag(next.tag);
      setExpiringFilter(next.expiringFilter);
      setNoticeFilter(next.noticeFilter);
      setDeadlineFilter(next.deadlineFilter);
      setDeadlineWindow(next.deadlineWindow);
      setNeedsReview(next.needsReview);
      setRenewalReviewNeeded(next.renewalReviewNeeded);
      setSource(next.source);
      setSortField(next.sortField);
      setSortOrder(next.sortOrder);
      setPage(next.page);
    }, [markReseeded]),
  });

  useEffect(() => {
    const params = new URLSearchParams();
    // The TRIMMED term, matching what buildContractQueryParams sends.
    const trimmedSearch = normalizeSearchValue(search);
    if (trimmedSearch) params.set('search', trimmedSearch);
    if (status) params.set('status', status);
    if (type) params.set('type', type);
    if (category) params.set('category', category);
    if (entity) params.set('entity', entity);
    if (counterparty) params.set('counterparty', counterparty);
    if (group) params.set('group', group);
    if (tag) params.set('tag', tag);
    if (expiringFilter) params.set('filter', 'expiring');
    else if (noticeFilter) params.set('filter', 'notice');
    else if (deadlineFilter) params.set('filter', 'deadline');
    // Round-trip the window so Back/reload/copy-link land on the same set.
    // Omitted at the default, like sort/order/page — a bare ?filter=deadline
    // stays a bare ?filter=deadline.
    if ((expiringFilter || noticeFilter || deadlineFilter) && deadlineWindow !== DEFAULT_DEADLINE_WINDOW) {
      params.set('deadline', String(deadlineWindow));
    }
    if (needsReview) params.set('needsReview', 'true');
    if (renewalReviewNeeded) params.set('renewalReviewNeeded', 'true');
    if (source) params.set('source', source);
    if (sortField && sortField !== 'updatedAt') params.set('sort', sortField);
    if (sortOrder && sortOrder !== 'desc') params.set('order', sortOrder);
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params, { replace: true });
    // Record what WE wrote so the re-seed above ignores our own write.
    markUrlWritten(params);
  }, [
    category,
    counterparty,
    deadlineFilter,
    deadlineWindow,
    entity,
    expiringFilter,
    group,
    // Stable (useCallback with no deps), so listing it does not re-run this.
    markUrlWritten,
    needsReview,
    noticeFilter,
    page,
    renewalReviewNeeded,
    search,
    setSearchParams,
    sortField,
    sortOrder,
    source,
    status,
    tag,
    type,
  ]);

  // The backend query params for the current filter/sort state — shared by
  // the list fetch (which adds paging) and the CSV export (which must NOT
  // page: the download is the whole filtered set).
  const buildFilterParams = useCallback(() => buildContractQueryParams({
    category,
    counterparty,
    deadlineFilter,
    deadlineWindow,
    entity,
    expiringFilter,
    group,
    needsReview,
    noticeFilter,
    renewalReviewNeeded,
    search,
    sortField,
    sortOrder,
    source,
    status,
    tag,
    type,
  }), [
    category,
    counterparty,
    deadlineFilter,
    deadlineWindow,
    entity,
    expiringFilter,
    group,
    needsReview,
    noticeFilter,
    renewalReviewNeeded,
    search,
    sortField,
    sortOrder,
    source,
    status,
    tag,
    type,
  ]);

  useEffect(() => {
    const params = buildFilterParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());

    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    // Stale-response guard: rapid filter/search changes fire overlapping
    // requests, and an earlier (slower) response must not clobber the latest.
    let cancelled = false;
    apiRequest(`/contracts?${params.toString()}`)
      .then((data) => {
        if (cancelled) return;
        setContracts(data.contracts || []);
        const nextPagination = data.pagination || { total: 0, totalPages: 1 };
        setPagination(nextPagination);
        setError(null);
        setLoading(false);
        // The result set can shrink under the current page (e.g. a bulk
        // status change empties the filtered page) — clamp back into range
        // rather than stranding the user on an empty page with no pager.
        const totalPages = nextPagination.totalPages || 1;
        if (page > totalPages) setPage(totalPages);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [buildFilterParams, page, pageSize, reloadKey, retryToken]);

  // Download the FULL filtered set (doc-116) — same filters as the list, no
  // paging. 'csv' or 'xlsx' (doc-238); the format is held rather than a plain
  // boolean so only the button that was clicked shows the busy label. Errors
  // propagate so the page can toast them.
  const [exportingFormat, setExportingFormat] = useState(null);
  const exportRegister = useCallback(async (format) => {
    setExportingFormat(format);
    try {
      await apiDownload(`/contracts/export.${format}?${buildFilterParams()}`, `docket-contracts.${format}`);
    } finally {
      setExportingFormat(null);
    }
  }, [buildFilterParams]);

  // Clears the selection synchronously (not just on the refetch tick) so the
  // bulk bar never lingers over rows that are being reloaded.
  const refresh = useCallback(() => {
    setSelectedIds(new Set());
    setReloadKey((key) => key + 1);
  }, []);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Header checkbox: select every row on the current page, or clear if they
  // all already are. Selection is page-scoped by design (no cross-page
  // "select all N matches" in v1).
  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const pageIds = contracts.map((c) => c.id);
      const allSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(pageIds);
    });
  }, [contracts]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    localStorage.setItem('contractsPageSize', newSize.toString());
    setPage(1);
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortOrder((order) => order === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setType('');
    setCategory('');
    setEntity('');
    setCounterparty('');
    setGroup('');
    setTag('');
    setExpiringFilter(false);
    setNoticeFilter(false);
    setDeadlineFilter(false);
    setDeadlineWindow(DEFAULT_DEADLINE_WINDOW);
    setNeedsReview(false);
    setRenewalReviewNeeded(false);
  };

  const hasFilters = !!(
    normalizeSearchValue(search) ||
    status ||
    type ||
    category ||
    entity ||
    counterparty ||
    group ||
    tag ||
    expiringFilter ||
    noticeFilter ||
    deadlineFilter ||
    needsReview ||
    renewalReviewNeeded
  );

  const categoryOptions = useMemo(
    () => [{ value: '', label: 'All categories' }, ...CONTRACT_CATEGORY_OPTIONS],
    [],
  );

  const statusTabs = useMemo(() => [
    { value: '', label: 'All statuses' },
    ...CONTRACT_STATUSES,
  ], []);

  return {
    category,
    categoryOptions,
    clearFilters,
    clearSelection,
    contracts,
    contractTypeOptions,
    deadlineFilter,
    deadlineWindow,
    deadlineWindowOptions: DEADLINE_WINDOW_OPTIONS,
    entity,
    entityOptions: LIGHTSPARK_ENTITIES,
    error,
    expiringFilter,
    exportRegister,
    exportingFormat,
    group,
    groupOptions,
    handlePageSizeChange,
    hasFilters,
    loading,
    lookupsDegraded,
    needsReview,
    noticeFilter,
    page,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    pagination,
    refresh,
    renewalReviewNeeded,
    retry,
    search,
    selectedIds,
    setCategory,
    setDeadlineFilter,
    setDeadlineWindow,
    setEntity,
    setExpiringFilter,
    setGroup,
    setNeedsReview,
    setNoticeFilter,
    setPage,
    setRenewalReviewNeeded,
    setSearch,
    setSource,
    setStatus,
    setTag,
    setType,
    sortField,
    sortOrder,
    source,
    status,
    statusCounts,
    statusTabs,
    tag,
    tagOptions,
    toggleSelectAllOnPage,
    toggleSelected,
    toggleSort,
    type,
  };
}
